import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Paper, 
  TableContainer, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import { 
  MeetingRoom as MeetingIcon, 
  Description as ActaIcon, 
  Visibility as ViewDocsIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
  FileDownload as ExcelIcon
} from '@mui/icons-material';
import { IMPROVEMENT_CHANNEL } from 'User/Application/Constants/PathsConstant';
// Servicios de dominio
import { getImprovementProposalList } from 'ImprovementProposal/Domain/Services/GetImprovementProposalListService';
import { getImprovementProposalMeetings } from 'ImprovementProposal/Domain/Services/GetImprovementProposalMeetingsService';
import { getDepartmentsList } from 'Department/Domain/Services/GetDepartmentsListService';
import { getDocuments } from 'Document/Domain/Services/GetDocumentsService';
import { downloadDocument } from 'Document/Domain/Services/DownloadDocumentService';
import Loader from 'UI/Application/Components/Loaders/Loader';
import { toast } from 'react-toastify';

export default function ImprovementProposalHistoryView() {
  const navigate = useNavigate();
  const [proposals, setProposals] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  // Dialogs
  const [openMeetingDialog, setOpenMeetingDialog] = React.useState(false);
  const [openDocsDialog, setOpenDocsDialog] = React.useState(false);
  const [openActaDialog, setOpenActaDialog] = React.useState(false);
  const [selectedProposal, setSelectedProposal] = React.useState(null);
  const [selectedMeeting, setSelectedMeeting] = React.useState(null);
  const [meetingsByProposal, setMeetingsByProposal] = React.useState({});
  const [documents, setDocuments] = React.useState([]);
  const [actas, setActas] = React.useState([]);
  // Actas previas agrupadas por reunión de origen
  const [prevActaGroups, setPrevActaGroups] = React.useState([]);
  const [openRejectionDialog, setOpenRejectionDialog] = React.useState(false);
  const [rejectionText, setRejectionText] = React.useState('');
  const getAssignedName = (p) => (p?.trackingUserName || p?.trackingUserId || '');
  
  // Descargar documento (vía botón) manteniendo auth
  const handleDownloadDocument = async (doc) => {
    const result = await downloadDocument(doc.path);
    if (!result || result.contentType.includes('application/json')) {
      toast.error('No se pudo descargar el archivo');
      return;
    }
    const url = window.URL.createObjectURL(result.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.fileName || 'documento';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };
  
  // Filtros
  const [filterId, setFilterId] = React.useState('');
  const [filterDateFrom, setFilterDateFrom] = React.useState('');
  const [filterDateTo, setFilterDateTo] = React.useState('');
  const [filterEndDateFrom, setFilterEndDateFrom] = React.useState('');
  const [filterEndDateTo, setFilterEndDateTo] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [filterCreator, setFilterCreator] = React.useState('');
  const [filterAssigned, setFilterAssigned] = React.useState('');
  const [filterDescription, setFilterDescription] = React.useState('');
  const [filterGoal, setFilterGoal] = React.useState('');
  const [filterProcess, setFilterProcess] = React.useState('');
  const [filterDepartments, setFilterDepartments] = React.useState([]); // array<int>

  // Departamentos (id -> nombre)
  const [departments, setDepartments] = React.useState([]); // [{id, name}]
  const departmentMap = React.useMemo(() => {
    const map = new Map();
    departments.forEach(d => map.set(d.id, d.name));
    return map;
  }, [departments]);

  // Cargar departamentos una sola vez
  React.useEffect(() => {
    (async () => {
      try {
        const resp = await getDepartmentsList({ limit: 500, offset: 0 });
        const list = Array.isArray(resp?.records) ? resp.records : [];
        // Esperamos que cada record tenga id y name (asunción). Si difiere, ajustar.
        setDepartments(list.map(r => ({ id: r.id, name: r.name || r.description || `Dept ${r.id}` })));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error cargando departamentos', e);
      }
    })();
  }, []);

  // Opciones únicas para autocompletar
  const creatorOptions = React.useMemo(() => {
    const set = new Set(
      (proposals || [])
        .map(p => (p?.createdByUserName || '').toString().trim())
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [proposals]);

  const assignedOptions = React.useMemo(() => {
    const set = new Set(
      (proposals || [])
        .map(p => (getAssignedName(p) || '').toString().trim())
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [proposals]);

  const loadProposals = React.useCallback(async () => {
    try {
      const response = await getImprovementProposalList({ limit: 1000, offset: 0 });
      const records = Array.isArray(response?.records) ? response.records : [];
      const closedProposals = records.filter(p => p.status === 'closed' || p.status === 'rejected');
      setProposals(closedProposals);
      // NO precargar reuniones aquí -> lazy cuando el usuario abre el diálogo
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error loading proposals:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
      loadProposals();
  }, [loadProposals]);

  const handleViewDocuments = async (proposal) => {
    try {
      const docs = await getDocuments({ className: 'ImprovementProposal', idClassName: proposal.id });
      setDocuments(Array.isArray(docs) ? docs : []);
      setSelectedProposal(proposal);
      setOpenDocsDialog(true);
    } catch (e) {
      toast.error('No se pudieron cargar los documentos');
    }
  };

  const handleViewActas = async (meeting) => {
    try {
      const docs = await getDocuments({ className: 'ImprovementProposalMeeting', idClassName: meeting.id });
      setActas(Array.isArray(docs) ? docs : []);
      const meetings = Array.isArray(meetingsByProposal[meeting.improvementProposalId]) ? meetingsByProposal[meeting.improvementProposalId] : [];
      const byId = new Map(meetings.map(m => [m.id, m]));
      const chain = [];
      let cursor = meeting.previousMeeting?.id;
      const guard = new Set();
      while (cursor && !guard.has(cursor)) {
        guard.add(cursor);
        const prev = byId.get(cursor);
        if (!prev) break;
        chain.push(prev);
        cursor = prev.previousMeeting?.id;
      }
      chain.reverse();
      const groups = await Promise.all(chain.map(async (prev) => {
        const prevDocs = await getDocuments({ className: 'ImprovementProposalMeeting', idClassName: prev.id });
        return { meetingId: prev.id, meetingDate: prev.meetingDate, docs: Array.isArray(prevDocs) ? prevDocs : [] };
      }));
      setPrevActaGroups(groups);
      setSelectedMeeting(meeting);
      setOpenActaDialog(true);
    } catch (e) {
      toast.error('No se pudieron cargar las actas');
    }
  };

  const handleViewMeetings = async (proposal) => {
    try {
      const meetings = await getImprovementProposalMeetings(proposal.id);
      setMeetingsByProposal((prev) => ({ ...prev, [proposal.id]: Array.isArray(meetings) ? meetings : [] }));
      setSelectedProposal(proposal);
      setOpenMeetingDialog(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error fetching meetings on demand:', e);
      toast.error('No se pudieron cargar las reuniones');
    }
  };

  const filteredProposals = proposals.filter(proposal => {
    if (filterId && !proposal.id.toString().includes(filterId)) return false;
    if (filterDateFrom) {
      const from = new Date(`${filterDateFrom}T00:00:00`);
      if (new Date(proposal.createdAt) < from) return false;
    }
    if (filterDateTo) {
      const to = new Date(`${filterDateTo}T23:59:59.999`);
      if (new Date(proposal.createdAt) > to) return false;
    }
    if (filterEndDateFrom) {
      const fromEnd = new Date(`${filterEndDateFrom}T00:00:00`);
      if (proposal.finishedAt && new Date(proposal.finishedAt) < fromEnd) return false;
    }
    if (filterEndDateTo) {
      const toEnd = new Date(`${filterEndDateTo}T23:59:59.999`);
      if (proposal.finishedAt && new Date(proposal.finishedAt) > toEnd) return false;
    }
    if (filterStatus && proposal.status !== filterStatus) return false;
    if (filterCreator) {
      const creator = (proposal.createdByUserName || '').toLowerCase();
      if (!creator.includes(filterCreator.toLowerCase())) return false;
    }
    if (filterAssigned) {
      const assigned = (getAssignedName(proposal) || '').toLowerCase();
      if (!assigned.includes(filterAssigned.toLowerCase())) return false;
    }
    if (filterDescription && !proposal.currentSituationDescription?.toLowerCase().includes(filterDescription.toLowerCase())) return false;
    if (filterGoal && !proposal.improvementGoal?.toLowerCase().includes(filterGoal.toLowerCase())) return false;
    if (filterProcess) {
      const proc = (proposal.relatedProcess || '').toLowerCase();
      if (!proc.includes(filterProcess.toLowerCase())) return false;
    }
    if (filterDepartments.length > 0) {
      const rel = Array.isArray(proposal.relatedDepartments) ? proposal.relatedDepartments : [];
      // Mostrar si tiene intersección con alguno de los seleccionados
      const hasAny = rel.some(id => filterDepartments.includes(id));
      if (!hasAny) return false;
    }
    return true;
  });

  const handleExportExcel = () => {
    const filteredData = filteredProposals.map(proposal => ({
      'ID': proposal.id,
      'Fecha creación': new Date(proposal.createdAt).toLocaleDateString(),
  'Fecha fin': proposal.finishedAt ? new Date(proposal.finishedAt).toLocaleDateString() : '-',
      'Estado': proposal.status === 'closed' ? 'Cerrada' : 'Rechazada',
      'Creado por': proposal.createdByUserName || 'N/A',
      'Asignado a': getAssignedName(proposal) || 'N/A',
      'Proceso relacionado': proposal.relatedProcess || '-',
      'Departamentos': (Array.isArray(proposal.relatedDepartments) && proposal.relatedDepartments.length > 0)
        ? proposal.relatedDepartments.map(did => departmentMap.get(did) || did).join('; ')
        : '-',
      'Descripción situación': proposal.currentSituationDescription || '-',
      '¿Qué se pretende mejorar?': proposal.improvementGoal || '-',
      'Motivo rechazo': proposal.status === 'rejected' ? (proposal.rejectionReason || '-') : '-',
      'Reducción costes anual': proposal.costReduction || '-',
      'Reducción tiempos anual': proposal.timeReduction || '-'
    }));

    // Crear CSV
    const headers = Object.keys(filteredData[0] || {});
    const csvContent = [
      headers.join(','),
      ...filteredData.map(row => headers.map(header => `"${row[header]}"`).join(','))
    ].join('\n');

    // Descargar archivo (eliminación segura del nodo para evitar NotFoundError removeChild)
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    try {
      link.href = url;
      link.download = `mejoras_cerradas_${new Date().toISOString().split('T')[0]}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
    } finally {
      // Eliminación resiliente: no asume que el nodo siga siendo hijo del body
      if (typeof link.remove === 'function') {
        link.remove();
      } else if (link.parentNode) {
        try {
          link.parentNode.removeChild(link);
        } catch (_) {
          // no-op
        }
      }
      // Revocar URL en el siguiente tick para no cortar descargas lentas en algunos navegadores
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    toast.success('Archivo exportado correctamente');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" mb={1}>
        <IconButton size="small" onClick={() => navigate(IMPROVEMENT_CHANNEL)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1, cursor: 'pointer' }} onClick={() => navigate(IMPROVEMENT_CHANNEL)}>Volver</Typography>
      </Box>
      <Typography variant="h5" mb={2}>Consulta mejoras cerradas</Typography>
      
      {/* Filtros (siempre visibles) */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filtros</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              label="ID" 
              value={filterId} 
              onChange={(e) => setFilterId(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              label="Fecha creación desde" 
              value={filterDateFrom} 
              onChange={(e) => setFilterDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              label="Fecha creación hasta" 
              value={filterDateTo} 
              onChange={(e) => setFilterDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              label="Fecha fin desde" 
              value={filterEndDateFrom} 
              onChange={(e) => setFilterEndDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              label="Fecha fin hasta" 
              value={filterEndDateTo} 
              onChange={(e) => setFilterEndDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filterStatus}
                label="Estado"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="closed">Cerrada</MenuItem>
                <MenuItem value="rejected">Rechazada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              size="small"
              options={creatorOptions}
              value={filterCreator || null}
              onChange={(e, v) => setFilterCreator(v || '')}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Creador" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              size="small"
              options={assignedOptions}
              value={filterAssigned || null}
              onChange={(e, v) => setFilterAssigned(v || '')}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Asignado a" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              label="Descripción situación" 
              value={filterDescription} 
              onChange={(e) => setFilterDescription(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField 
              fullWidth 
              size="small" 
              label="¿Qué se pretende mejorar?" 
              value={filterGoal} 
              onChange={(e) => setFilterGoal(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Proceso relacionado"
              value={filterProcess}
              onChange={(e) => setFilterProcess(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              options={departments}
              getOptionLabel={(o) => o.name || ''}
              value={departments.filter(d => filterDepartments.includes(d.id))}
              onChange={(e, values) => setFilterDepartments(values.map(v => v.id))}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Departamentos" placeholder="Filtrar..." />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Botón Exportar Excel */}
      <Box mb={2}>
        <Button 
          variant="contained" 
          startIcon={<ExcelIcon />}
          onClick={handleExportExcel}
          disabled={filteredProposals.length === 0}
        >
          Exportar a Excel
        </Button>
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Fecha creación</TableCell>
              <TableCell align="center">Fecha fin</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Creado por</TableCell>
              <TableCell align="center">Asignado a</TableCell>
              <TableCell align="center">Proceso relacionado</TableCell>
              <TableCell align="center">Departamentos</TableCell>
              <TableCell align="center">Descripción situación</TableCell>
              <TableCell align="center">¿Qué se pretende mejorar?</TableCell>
              <TableCell align="center">Reducción costes anual</TableCell>
              <TableCell align="center">Reducción tiempos anual</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProposals.map((proposal) => (
              <TableRow key={`proposal-${proposal.id}`}>
                <TableCell align="center">
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <Tooltip title="Ver detalle de la propuesta">
                      <Button
                        variant="text"
                        size="small"
                        sx={{ mt: 0.5, textTransform: 'none' }}
                        onClick={() => navigate(`/private/user/improvement-proposal/${proposal.id}`)}
                      >
                        {proposal.id}
                      </Button>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {new Date(proposal.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="center">
                  {proposal.finishedAt ? new Date(proposal.finishedAt).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell align="center">
                  <Box 
                    sx={{ 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1, 
                      backgroundColor: proposal.status === 'closed' ? 'success.light' : 'error.light',
                      color: proposal.status === 'closed' ? 'success.dark' : 'error.dark',
                      fontWeight: 'bold'
                    }}
                  >
                    {proposal.status === 'closed' ? 'Cerrada' : 'Rechazada'}
                  </Box>
                </TableCell>
                <TableCell align="center">{proposal.createdByUserName || 'N/A'}</TableCell>
                <TableCell align="center">{getAssignedName(proposal) || 'N/A'}</TableCell>
                <TableCell align="center">{proposal.relatedProcess || '-'}</TableCell>
                <TableCell align="center">
                  {Array.isArray(proposal.relatedDepartments) && proposal.relatedDepartments.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                      {proposal.relatedDepartments.map(id => {
                        const name = departmentMap.get(id) || id;
                        return (
                          <Box
                            key={id}
                            component="span"
                            sx={{
                              px: 1.2,
                              py: 0.4,
                              fontSize: '0.65rem',
                              lineHeight: 1.2,
                              bgcolor: 'grey.200',
                              color: 'grey.900',
                              borderRadius: '999px',
                              fontWeight: 500,
                              display: 'inline-block',
                              maxWidth: '140px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={String(name)}
                          >
                            {name}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="center">
                  {proposal.currentSituationDescription || '-'}
                </TableCell>
                <TableCell align="center">
                  {proposal.improvementGoal || '-'}
                </TableCell>
                <TableCell align="center">
                  {proposal.costReduction ? `${proposal.costReduction}€` : '-'}
                </TableCell>
                <TableCell align="center">
                  {proposal.timeReduction ? `${proposal.timeReduction}h` : '-'}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Reuniones">
                    <IconButton 
                      color="primary" 
                      onClick={() => handleViewMeetings(proposal)}
                    >
                      <MeetingIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ver documentos">
                    <IconButton 
                      color="info" 
                      onClick={() => handleViewDocuments(proposal)}
                    >
                      <ViewDocsIcon />
                    </IconButton>
                  </Tooltip>
                  {proposal.status === 'rejected' && proposal.rejectionReason && (
                    <Button 
                      size="small" 
                      color="error" 
                      variant="outlined"
                      onClick={() => {
                        setRejectionText(proposal.rejectionReason);
                        setOpenRejectionDialog(true);
                      }}
                      sx={{ ml: 1 }}
                    >
                      Motivo rechazo
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Reuniones */}
      <Dialog open={openMeetingDialog} onClose={() => setOpenMeetingDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Reuniones - Propuesta #{selectedProposal?.id}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha inicio</TableCell>
                  <TableCell>Fecha fin</TableCell>
                  <TableCell>Recurrencia</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {((meetingsByProposal[selectedProposal?.id]) || []).map((meeting) => (
                  <TableRow key={`meeting-${meeting.id}`}>
                    <TableCell>{new Date(meeting.meetingDate).toLocaleString()}</TableCell>
                    <TableCell>
                      {meeting.recurrenceEndAt ? new Date(meeting.recurrenceEndAt).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{meeting.recurrenceNote || (meeting.recurrenceDays > 0 ? `${meeting.recurrenceDays} días` : 'Única')}</TableCell>
                    <TableCell>{meeting.description || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Actas">
                        <IconButton size="small" onClick={() => handleViewActas(meeting)}>
                          <ActaIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMeetingDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Actas */}
      <Dialog open={openActaDialog} onClose={() => setOpenActaDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Actas - Reunión #{selectedMeeting?.id}</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            {prevActaGroups.length === 0 ? (
              <Typography variant="body2">Sin actas previas</Typography>
            ) : (
              prevActaGroups.map((group) => (
                <Box key={`group-${group.meetingId}`} mb={2} p={2} border={1} borderColor="divider" borderRadius={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Viene de la reunión del {new Date(group.meetingDate).toLocaleString()} (#{group.meetingId})
                  </Typography>
                  {group.docs.length === 0 ? (
                    <Typography variant="body2">Sin actas en esa reunión</Typography>
                  ) : (
                    group.docs.map((doc, index) => (
                      <Box key={`prev-${group.meetingId}-${doc.id || doc.path || doc.fileName || index}`} display="flex" alignItems="center" justifyContent="space-between" p={1} border={1} borderColor="divider" mb={1}>
                        <Typography>{doc.fileName || `Acta previa ${index + 1}`}</Typography>
                        <Button size="small" variant="outlined" onClick={() => handleDownloadDocument(doc)}>
                          Descargar
                        </Button>
                      </Box>
                    ))
                  )}
                </Box>
              ))
            )}
          </Box>
          {actas.length === 0 ? (
            <Typography>No hay actas adjuntas</Typography>
          ) : (
            <Box>
              {actas.map((acta, index) => (
                <Box key={`acta-${acta.id || acta.path || acta.fileName || index}`} display="flex" alignItems="center" justifyContent="space-between" p={1} border={1} borderColor="divider" mb={1}>
                  <Typography>{acta.fileName || `Acta ${index + 1}`}</Typography>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadDocument(acta)}>
                    Descargar
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenActaDialog(false)} sx={{ '&:hover': { color: 'error.main', backgroundColor: 'transparent' } }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Documentos */}
      <Dialog open={openDocsDialog} onClose={() => setOpenDocsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Documentos - Propuesta #{selectedProposal?.id}</DialogTitle>
        <DialogContent>
          {documents.length === 0 ? (
            <Typography>No hay documentos adjuntos</Typography>
          ) : (
            <Box>
              {documents.map((doc, index) => (
                <Box key={`doc-${doc.id || doc.path || doc.fileName || index}`} display="flex" alignItems="center" justifyContent="space-between" p={1} border={1} borderColor="divider" mb={1}>
                  <Typography>{doc.fileName || `Documento ${index + 1}`}</Typography>
                  <Button size="small" variant="outlined" onClick={() => handleDownloadDocument(doc)}>
                    Descargar
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDocsDialog(false)} sx={{ '&:hover': { color: 'error.main', backgroundColor: 'transparent' } }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Motivo de rechazo */}
      <Dialog open={openRejectionDialog} onClose={() => setOpenRejectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Motivo del rechazo</DialogTitle>
        <DialogContent>
          <Typography whiteSpace="pre-wrap">{rejectionText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectionDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


